import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, forgetAdminTenantSlug } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout API failure shouldn't block client-side logout; user gets logged out locally regardless.
      console.warn("[auth] logout API call failed (continuing local logout):", e?.message);
    }
    forgetAdminTenantSlug();
    localStorage.removeItem("solunary_admin_email");
    setUser(null);
  }, []);

  useEffect(() => {
    // GIS (Google Identity Services) posts the session directly from GoogleLoginButton
    // before calling refresh() itself — there is no hash-based OAuth callback to skip
    // here anymore, so the initial mount always checks for an existing session.
    refresh();
  }, [refresh]);

  useEffect(() => {
    // BUG-243: If the authenticated user changes, their inherited tenant slug must be cleared.
    if (!loading && user) {
      const storedEmail = localStorage.getItem("solunary_admin_email");
      if (storedEmail && storedEmail !== user.email) {
        forgetAdminTenantSlug();
      }
      // Always remember who the current slug belongs to
      localStorage.setItem("solunary_admin_email", user.email);
    }
  }, [user, loading]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
